import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import type { components } from '../api/schema';
import { unwrap } from '../api/unwrap';
import { buildListQuerySerializer } from '../lib/list-query';

export type RecipientStatus = components['schemas']['CampaignRecipient']['status'];

export interface RecipientRecord {
  id: string;
  campaignId: string;
  tradeLinkId: string;
  includedAt: string;
  status: RecipientStatus;
  quarantinedBy: string | null;
  quarantinedReason: string | null;
  quarantinedAt: string | null;
  resolvedAt: string | null;
  // Plan 08-03 — enrichment join (LEFT JOIN trade_links + LATERAL latest
  // trade). `tradeLinkUrl` always populated when the link still exists;
  // `sentAt` / `lastError` come from the latest matching trade row, both
  // null when no trade has been attempted yet.
  tradeLinkUrl: string | null;
  sentAt: string | null;
  lastError: string | null;
}

export interface RecipientListResult {
  items: RecipientRecord[];
  page: number;
  pageSize: number;
  total: number;
}

export interface RecipientsFilter {
  status?: RecipientStatus;
  page?: number;
  pageSize?: number;
}

export const recipientsKeys = {
  list: (campaignId: string, filter: RecipientsFilter) =>
    ['campaigns', campaignId, 'recipients', filter] as const,
};

// Phase 8.5-04 UX-02 — bracketed list query (ADR-014).
export const useCampaignRecipients = (
  campaignId: string | undefined,
  filter: RecipientsFilter = {},
) =>
  useQuery({
    queryKey: campaignId
      ? recipientsKeys.list(campaignId, filter)
      : ['campaigns', 'pending', 'recipients'],
    enabled: Boolean(campaignId),
    queryFn: async (): Promise<RecipientListResult> => {
      if (!campaignId) throw new Error('campaign id required');
      const bracketed: { status?: RecipientStatus } = {};
      if (filter.status !== undefined) bracketed.status = filter.status;
      const result = await api.GET('/api/campaigns/{id}/recipients', {
        params: {
          path: { id: campaignId },
          query: {
            sort: 'includedAt',
            page: String(filter.page ?? 1),
            pageSize: String(filter.pageSize ?? 50),
          },
        },
        querySerializer: buildListQuerySerializer(bracketed),
      });
      return unwrap(result);
    },
  });

// Phase 8.5-05 — recipient action mutations (ADR-014). Backend endpoints
// from 8.5-03; UI surface lands here. Each mutation invalidates
// `['campaigns', campaignId, 'recipients']` so the list refetches with
// the new status. We don't optimistically update — quarantine/resolve/
// retry are infrequent operator actions and the round-trip is fast.
const invalidateRecipients = (
  qc: ReturnType<typeof useQueryClient>,
  campaignId: string,
): Promise<void> => qc.invalidateQueries({ queryKey: ['campaigns', campaignId, 'recipients'] });

export const useQuarantineRecipient = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; reason: string }): Promise<RecipientRecord> => {
      const result = await api.POST('/api/campaign-recipients/{id}/quarantine', {
        params: { path: { id: args.id } },
        body: { reason: args.reason },
      });
      return unwrap(result).recipient as RecipientRecord;
    },
    onSuccess: () => invalidateRecipients(qc, campaignId),
  });
};

export const useResolveRecipient = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<RecipientRecord> => {
      const result = await api.POST('/api/campaign-recipients/{id}/resolve', {
        params: { path: { id } },
      });
      return unwrap(result).recipient as RecipientRecord;
    },
    onSuccess: () => invalidateRecipients(qc, campaignId),
  });
};

export const useRetryRecipient = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<RecipientRecord> => {
      const result = await api.POST('/api/campaign-recipients/{id}/retry', {
        params: { path: { id } },
      });
      return unwrap(result).recipient as RecipientRecord;
    },
    onSuccess: () => invalidateRecipients(qc, campaignId),
  });
};
